import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItem,
  Popover,
  Typography,
} from '@mui/material';
import { batchesAPI } from '../services/batches';
import { buildBatchSessionPath } from '../utils/teacherSessionNavigation';

/**
 * Shared hover controller for batch → students preview popover
 * (Batches list + teacher calendar).
 *
 * @returns {{
 *   state: { open: boolean, anchorEl: Element|null, batch: {id:string, name?:string}|null },
 *   open: (anchorEl: Element, batch: {id:string, name?:string}) => void,
 *   close: () => void,
 *   onTriggerLeave: () => void,
 *   keepOpen: () => void,
 * }}
 */
export function useBatchStudentsHover() {
  const timerRef = useRef(null);
  const [state, setState] = useState({
    open: false,
    anchorEl: null,
    batch: null,
  });

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearTimer(), [clearTimer]);

  const open = useCallback(
    (anchorEl, batch) => {
      if (!batch?.id || !anchorEl) return;
      clearTimer();
      timerRef.current = setTimeout(() => {
        setState({ open: true, anchorEl, batch });
        timerRef.current = null;
      }, 250);
    },
    [clearTimer]
  );

  const close = useCallback(() => {
    clearTimer();
    setState({ open: false, anchorEl: null, batch: null });
  }, [clearTimer]);

  /** Leaving the trigger: delay close so pointer can move into the popover. */
  const onTriggerLeave = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      setState((prev) => (prev.open ? { open: false, anchorEl: null, batch: null } : prev));
      timerRef.current = null;
    }, 120);
  }, [clearTimer]);

  /** Entering the popover cancels a pending close. */
  const keepOpen = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  return { state, open, close, onTriggerLeave, keepOpen };
}

/**
 * Students preview popover — same UX as the batch list name hover.
 * Pass `hover` from `useBatchStudentsHover()`.
 */
export default function BatchStudentsHoverPopover({ hover, limit = 10 }) {
  const navigate = useNavigate();
  const { state, close, keepOpen } = hover;
  const batchId = state.batch?.id || '';

  const studentsPreviewQuery = useQuery(
    ['batchStudentsPreview', batchId, limit],
    () => batchesAPI.getStudentsPreview(batchId, { limit }),
    {
      enabled: !!batchId && state.open,
      staleTime: 2 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  const previewStudents = studentsPreviewQuery.data?.data?.data?.students || [];

  return (
    <Popover
      open={state.open}
      anchorEl={state.anchorEl}
      onClose={close}
      disableRestoreFocus
      sx={{ pointerEvents: 'none' }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{
        sx: { width: 360, p: 1, pointerEvents: 'auto' },
        onMouseEnter: keepOpen,
        onMouseLeave: close,
      }}
    >
      <Box sx={{ px: 1, pt: 0.5, pb: 1 }}>
        <Typography variant="subtitle2" fontWeight={800}>
          Students — {state.batch?.name || 'Batch'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Showing up to {limit} students
        </Typography>
      </Box>

      {studentsPreviewQuery.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={18} />
        </Box>
      ) : studentsPreviewQuery.isError ? (
        <Alert severity="error" sx={{ mx: 1, mb: 1 }}>
          Failed to load students
        </Alert>
      ) : previewStudents.length === 0 ? (
        <Box sx={{ px: 1, pb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            No students assigned.
          </Typography>
        </Box>
      ) : (
        <List dense disablePadding sx={{ px: 1, pb: 1 }}>
          {previewStudents.map((s) => (
            <ListItem key={s.student_id} disableGutters sx={{ py: 0.25 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap title={s.student_name || ''}>
                  {s.student_name}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  title={s.student_email || ''}
                >
                  {s.student_email || '—'}
                  {s.age_group ? ` · ${s.age_group}` : ''}
                </Typography>
              </Box>
            </ListItem>
          ))}
        </List>
      )}

      <Divider sx={{ my: 0.5 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pb: 0.5 }}>
        <Button
          size="small"
          onClick={() => {
            if (state.batch?.id) {
              const path = buildBatchSessionPath(state.batch.id, state.batch.sessionId, {
                sessionDate: state.batch.sessionDate,
                startTime: state.batch.startTime,
                endTime: state.batch.endTime,
              });
              if (path) navigate(path);
            }
            close();
          }}
        >
          Open batch
        </Button>
      </Box>
    </Popover>
  );
}
